import React, { useEffect } from 'react'

import LogoContainer from '../../../components/LogoContainer'
import CourseDisplayName from '../../../components/CourseDisplayName'
import QuestionsPerTrainerAndSubject from './QuestionsPerTrainerAndSubject'
import SavePollButton from './SavePollButton'
import OkButton from './OkButton'
import { SAVE_ERROR_ID } from './SaveErrorMessage'
import questionIds from './Question/questionIds'

import { reduceToCommonValues, mapObjectToArray } from '../../../utils'
import {
  isRequiredQuestion,
  visibleMissingQuestionIds,
  visibleLowMarkGroups,
  lowMarkGroupLabel,
} from '../../../state/validation'
import t from '../../../translations'
import { Typography } from '@material-ui/core'

const styles = {
  requiredLegend: {
    margin: '0 auto',
    maxWidth: 1112,
    padding: '0 2.7rem',
    boxSizing: 'border-box',
  },
}

// Klucz grupy z reduceToCommonValues to tekst: pytania ogólne (bez prowadzącego) trafiają pod 'undefined' lub 'null'.
const isTrainerKey = (key) => key !== 'undefined' && key !== 'null'

// Pytania, do których przenosimy fokus po nieudanej próbie wysłania: brakujące odpowiedzi, a gdy ich nie ma —
// pola tekstowe przy niskiej ocenie. Z migawki walidacji, więc fokus ustawia się raz na próbę.
const focusCandidates = (validation) => {
  if (!validation) return []
  if (validation.missingQuestionIds.length > 0) return validation.missingQuestionIds

  return validation.lowMarkGroups.reduce((all, group) => all.concat(group.textQuestionIds), [])
}

// Przewija do pierwszego (najwyżej na stronie) pytania do poprawy i ustawia fokus na jego pierwszym polu.
const focusFirstQuestion = (candidateIds) => {
  const blocks = candidateIds
    .map(questionId => document.getElementById(questionIds(questionId).block))
    .filter(Boolean)
    .sort((a, b) => a.getBoundingClientRect().top - b.getBoundingClientRect().top)
  const block = blocks[0]

  if (!block) return

  window.scrollTo(0, block.getBoundingClientRect().top + window.pageYOffset - 16)
  const input = block.querySelector('input')
  if (input) input.focus({ preventScroll: true })
}

const Poll = (props) => {
  // trainersData bywa null (dane prowadzących niewczytane): sekcje bez nazwisk zamiast białej strony.
  const trainersData = props.trainersData || []
  const allQuestions = props.questions || []
  const answers = props.answers || {}

  const missingQuestionIds = visibleMissingQuestionIds(props.validation, answers)
  const lowMarkGroups = visibleLowMarkGroups(props.validation, answers)
  const lowMarkHintQuestionIds = lowMarkGroups.reduce((all, group) => all.concat(group.textQuestionIds), [])
  const lowMarkLabels = lowMarkGroups
    .map(group => lowMarkGroupLabel(group, allQuestions, trainersData))
    .filter((label, index, all) => all.indexOf(label) === index)

  const attempt = props.validation ? props.validation.attempt : 0
  const focusIds = focusCandidates(props.validation).join('\n')

  useEffect(() => {
    if (attempt > 0 && focusIds) focusFirstQuestion(focusIds.split('\n'))
  }, [attempt, focusIds])

  // Kolejny numer sekcji (prowadzący × temat) na identyfikator nagłówka; temat może mieć spacje, więc nie nadaje się
  // do IDREF w aria-labelledby.
  let sectionIndex = 0

  // Na czas zapisu formularz jest odmontowany, więc po błędzie wraca od góry strony. Komunikat o błędzie stoi nad
  // przyciskiem wysyłania — przewijamy do niego, żeby był widoczny razem z przyciskiem.
  useEffect(() => {
    const message = props.saveError && document.getElementById(SAVE_ERROR_ID)
    if (message) message.scrollIntoView({ block: 'center' })
  }, [props.saveError])

  return (
    <div>
      <LogoContainer
        colored={true}
      >
        <Typography paragraph={true} variant={'h4'} component={'h1'} style={{ fontWeight: 700 }}>
          {props.name}
        </Typography>
        <CourseDisplayName
          course={props.courseObj}
        />
      </LogoContainer>
      {
        <div>
          {
            props.displayOkButton ?
              <OkButton
                onConfirm={() => props.okButton()}
              />
              :
              null
          }
          {
            allQuestions.some(isRequiredQuestion) ?
              <Typography variant={'body2'} style={styles.requiredLegend}>
                {t.pollView.requiredLegend}
              </Typography>
              :
              null
          }
          {
            props.questions && mapObjectToArray(
              reduceToCommonValues(props.questions, 'trainer')
            ).map(questionsPerTrainer => {
              const trainerUid = questionsPerTrainer.key
              const questions = questionsPerTrainer.value

              return mapObjectToArray(
                reduceToCommonValues(questions, 'subject')
              ).map(questionsPerSubject => {
                const subject = questionsPerSubject.key
                const questions = questionsPerSubject.value

                const hasTrainer = isTrainerKey(trainerUid)
                const trainer = hasTrainer ? trainersData.find(trainer => trainer.trainerUid === trainerUid) : undefined
                const sectionId = `sekcja-${sectionIndex}`
                sectionIndex += 1

                return (
                  <QuestionsPerTrainerAndSubject
                    key={trainerUid}
                    trainer={trainer}
                    trainerMissing={hasTrainer && !trainer}
                    subject={subject}
                    questions={questions}
                    setAnswer={props.setAnswer}
                    answers={props.answers}
                    missingQuestionIds={missingQuestionIds}
                    lowMarkHintQuestionIds={lowMarkHintQuestionIds}
                    savedQuestionUids={props.savedQuestionUids || {}}
                    sectionId={sectionId}
                  />
                )
              })
            })
          }
          <SavePollButton
            onClick={() => props.saveAnswers()}
            missingCount={missingQuestionIds.length}
            lowMarkLabels={lowMarkLabels}
            saveError={props.saveError}
          />
        </div>
      }
    </div>
  )
}

export default Poll
